import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useGlobalConfig } from 'context/GlobalContext';
import { DataScenariosApi, CreateDataScenarioRequest, DataScenarioDetailsResponse } from 'apis/dataScenariosAPI';
import { CMSPilotApis } from 'apis/cmsPilotAPI';
import { toast } from 'utils/toast';

// Locale type from API
interface LocaleOption {
  code: string;
  name: string;
}

const LineArrowIcon = (props: any) => (
  <Box
    {...props}
    sx={{
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...props.sx
    }}
  >
    <Box
      component="svg"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity="0.6"
      />
    </Box>
  </Box>
);


interface Payload {
  id: string;
  endpoint: string;
  response: string;
  expanded: boolean;
  isValidJson: boolean;
}

interface EditDataScenariosProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  pilotId: string;
  scenarioData?: ScenarioData;
  elementKey?: string; 
}

interface ScenarioData {
  scenarioId: string;
  label: string;
  configLevel: string;
  templateType: string;
  locale: string;
  templateName: string;
  elementName: string;
  elementNames?: string[];
  fuelType: string;
  payloads: Array<{ endpoint: string; response: string }>;
}

export const EditDataScenarios: React.FC<EditDataScenariosProps> = ({
  open,
  onClose,
  onSave,
  pilotId,
  scenarioData,
  elementKey 
}) => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  
  const [scenarioId, setScenarioId] = useState('');
  const [label, setLabel] = useState('');
  const [configLevel, setConfigLevel] = useState('Project');
  const [templateType, setTemplateType] = useState('Email');
  const [locale, setLocale] = useState('English (US)');
  const [templateNameValue, setTemplateNameValue] = useState('');
  const [elementNameValue, setElementNameValue] = useState('');
  const [elementNames, setElementNames] = useState<string[]>([]);
  const [fuelType, setFuelType] = useState('ELECTRIC');
  const [payloads, setPayloads] = useState<Payload[]>([]);
  
  const [locales, setLocales] = useState<LocaleOption[]>([]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  useEffect(() => {
    const fetchLocales = async () => {
      try {
        const response = await CMSPilotApis.getSupportedLocales(rc);
        if (!response.error && response.data) {
          setLocales(response.data);
        }
      } catch (error) {
        console.error('[EditDataScenarios] Failed to fetch locales:', error);
      }
    };
    
    fetchLocales();
  }, [rc]);

  const getLocaleDisplayName = (code: string): string => {
    const found = locales.find(l => l.code === code);
    return found ? found.name : code;
  };

  const getLocaleCode = (name: string): string => {
    const found = locales.find(l => l.name === name);
    if (found) return found.code;
    
    // Fallback mapping for common locale display names
    const localeMap: Record<string, string> = {
      'English (US)': 'en_US',
      'English (UK)': 'en_GB',
      'Spanish': 'es_ES',
      'Spanish (US)': 'es_US',
      'French': 'fr_FR',
      'French (Canada)': 'fr_CA',
      'German': 'de_DE',
      'Portuguese': 'pt_PT',
      'Portuguese (Brazil)': 'pt_BR',
      'Chinese (Simplified)': 'zh_CN',
      'Chinese (Traditional)': 'zh_TW',
      'Japanese': 'ja_JP',
      'Korean': 'ko_KR',
    };
    
    return localeMap[name] || name;
  };

  // Helper function to validate JSON
  const isValidJson = (str: string): boolean => {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  // Helper function to format JSON
  const formatJson = (str: string): string => {
    try {
      const parsed = JSON.parse(str);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return str; 
    }
  };

  useEffect(() => {
    const fetchScenarioDetails = async () => {
      if (!scenarioData || !open || !pilotId || !scenarioData.scenarioId) return;
      
      setIsFetchingDetails(true);
      
      try {
        const response = await DataScenariosApi.getDataScenarioDetails(rc, {
          pilotId: pilotId,
          scenarioId: scenarioData.scenarioId
        });
        
        if (response.success && response.data) {
          const details = response.data;
          
          // Populate all fields from API response only
          setScenarioId(details.scenarioId || '');
          setLabel(details.scenarioDisplayName || '');
          setConfigLevel('Project');
          setTemplateType(details.productType || 'Email');
          const displayLocale = getLocaleDisplayName(details.scenarioLocale) || details.scenarioLocale || '';
          setLocale(displayLocale);
          setTemplateNameValue(details.templateName || '');
          setFuelType(details.fuelType || '');
          
          if (details.elementNames && details.elementNames.length > 0) {
            setElementNames(details.elementNames);
            
            let matchedElementName = '';
            if (elementKey && details.elementNames.includes(elementKey)) {
              matchedElementName = elementKey;
            } else {
              const specificElements = details.elementNames.filter(name => name !== 'FULL_TEMPLATE');
              if (specificElements.length > 0) {
                matchedElementName = specificElements[0];
              } else {
                matchedElementName = details.elementNames[0] || 'FULL_TEMPLATE';
              }
            }
            setElementNameValue(matchedElementName);
          }
          
          // Populate payloads from API response
          if (details.payloads && details.payloads.length > 0) {
            setPayloads(
              details.payloads.map((p, index) => ({
                id: `payload-${index}`,
                endpoint: p.templateTypeName || '',
                response: JSON.stringify(p.payload, null, 2),
                expanded: index === 0,
                isValidJson: true
              }))
            );
          } else {
            setPayloads([{
              id: '1',
              endpoint: '',
              response: '{}',
              expanded: true,
              isValidJson: true
            }]);
          }
        }
      } catch (error) {
        console.error('[EditDataScenarios] Failed to fetch scenario details:', error);
      } finally {
        setIsFetchingDetails(false);
      }
    };
    
    fetchScenarioDetails();
  }, [scenarioData, open, pilotId, rc]);

  const handleTogglePayload = (id: string) => {
    setPayloads(prevPayloads =>
      prevPayloads.map(p => 
        p.id === id ? { ...p, expanded: !p.expanded } : p
      )
    );
  };

  const handleUpdatePayload = (id: string, field: 'endpoint' | 'response', value: string) => {
    setPayloads(prevPayloads =>
      prevPayloads.map(p => 
        p.id === id ? { 
          ...p, 
          [field]: value,
          isValidJson: field === 'response' ? isValidJson(value) : p.isValidJson
        } : p
      )
    );
  };

  const handleFormatJson = (id: string) => {
    setPayloads(prevPayloads =>
      prevPayloads.map(p => {
        if (p.id === id && isValidJson(p.response)) {
          return { ...p, response: formatJson(p.response) };
        }
        return p;
      })
    );
  };

  // Validate form before submission
  const validateForm = (): string | null => {
    // Validate payloads
    for (let i = 0; i < payloads.length; i++) {
      const payload = payloads[i];
      if (!payload.endpoint.trim()) {
        return `Payload ${i + 1}: Endpoint is required`;
      }
      if (!isValidJson(payload.response)) {
        return `Payload ${i + 1}: Response must be valid JSON`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsLoading(true);

    try {
      const transformedPayloads = payloads.map(p => ({
        templateTypeName: p.endpoint.trim(),
        payload: JSON.parse(p.response)
      }));

      let finalElementNames: string[];
      if (elementNames.length > 0) {
        finalElementNames = elementNames;
      } else if (elementNameValue.trim()) {
        finalElementNames = [elementNameValue.trim()];
      } else {
        finalElementNames = ['FULL_TEMPLATE'];
      }

      const requestData: CreateDataScenarioRequest = {
        pilotId: pilotId,
        scenarioId: scenarioId,
        scenarioDisplayName: label,
        productType: templateType,
        scenarioLocale: getLocaleCode(locale) || 'en_US',
        fuelType: fuelType,
        templateName: templateNameValue,
        elementNames: finalElementNames,
        defaultScenario: false,
        userClusters: ['DEFAULT'],
        scenarioDocumentation: `Updating the Data scenario ${label} - ${scenarioId}`,
        payloads: transformedPayloads
      };

      console.log('[EditDataScenarios] Updating scenario with data:', requestData);

      const response = await DataScenariosApi.createDataScenarioWithPayloads(rc, requestData);

      if (response.success) {
        toast.success(`Scenario "${label}" updated successfully`);
        onSave?.();
        handleClose();
      } else {
        toast.error(response.error || 'Failed to update scenario');
      }
    } catch (error: any) {
      console.error('[EditDataScenarios] Error updating scenario:', error);
      toast.error(error.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3,
          maxWidth: '800px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        pb: 2,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Edit Scenario: {label || 'Default'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Modify the JSON payload for this scenario. Only response data can be edited.
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={isLoading}
          sx={{ 
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {isFetchingDetails ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
              Loading scenario details...
            </Typography>
          </Box>
        ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Scenario ID and Label Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Scenario ID
              </Typography>
              <TextField
                value={scenarioId}
                disabled
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Label
              </Typography>
              <TextField
                value={label}
                disabled
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                  }
                }}
              />
            </Box>
          </Box>

          {/* Config Level, Template Type, Locale Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Config Level
              </Typography>
              <TextField
                value={configLevel}
                disabled
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Template Type
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={templateType}
                  disabled
                  IconComponent={LineArrowIcon}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiSelect-icon': {
                      marginRight: 0.5,
                      color: theme.palette.text.secondary
                    },
                    '& .MuiSelect-select.Mui-disabled': {
                      WebkitTextFillColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                    }
                  }}
                >
                  <MenuItem value="Email">Email</MenuItem>
                  <MenuItem value="SMS">SMS</MenuItem>
                  <MenuItem value="HER">HER</MenuItem>
                  <MenuItem value="Web">Web</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Locale
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={locale}
                  disabled
                  IconComponent={LineArrowIcon}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiSelect-icon': {
                      marginRight: 0.5,
                      color: theme.palette.text.secondary
                    },
                    '& .MuiSelect-select.Mui-disabled': {
                      WebkitTextFillColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                    }
                  }}
                >
                  {locales.length > 0 ? (
                    locales.map((loc) => (
                      <MenuItem key={loc.code} value={loc.name}>
                        {loc.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={locale}>{locale}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Template Name, Element Name, Fuel Type Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Template Name
              </Typography>
              <TextField
                value={templateNameValue}
                disabled
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Element Name
              </Typography>
              <TextField
                value={elementNameValue}
                disabled
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Fuel Type
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={fuelType}
                  disabled
                  IconComponent={LineArrowIcon}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiSelect-icon': {
                      marginRight: 0.5,
                      color: theme.palette.text.secondary
                    },
                    '& .MuiSelect-select.Mui-disabled': {
                      WebkitTextFillColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                    }
                  }}
                >
                  <MenuItem value="ELECTRIC">ELECTRIC</MenuItem>
                  <MenuItem value="GAS">GAS</MenuItem>
                  <MenuItem value="DUAL">DUAL</MenuItem>
                  <MenuItem value="WATER">WATER</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Scenario Payloads Section */}
          <Box sx={{ mt: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
              Scenario Payloads
            </Typography>

            {payloads.map((payload, index) => (
              <Accordion
                key={payload.id}
                expanded={payload.expanded}
                onChange={() => handleTogglePayload(payload.id)}
                sx={{
                  mb: 1,
                  '&:before': { display: 'none' },
                  boxShadow: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  '&.Mui-expanded': { margin: '0 0 8px 0' }
                }}
              >
                <AccordionSummary
                  expandIcon={
                    payload.expanded ? (
                      <ExpandLessIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                    )
                  }
                  sx={{
                    minHeight: 36,
                    '&.Mui-expanded': { minHeight: 36 },
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      my: 0.5
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Payload {index + 1}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Endpoint
                      </Typography>
                      <TextField
                        value={payload.endpoint}
                        disabled
                        placeholder="/api/v1/user-data"
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                          }
                        }}
                      />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Response (JSON) <span style={{ color: theme.palette.error.main }}>*</span>
                          </Typography>
                          {/* JSON validation indicator */}
                          {payload.response && (
                            <Tooltip title={payload.isValidJson ? 'Valid JSON' : 'Invalid JSON'}>
                              {payload.isValidJson ? (
                                <CheckCircleOutlineIcon 
                                  sx={{ fontSize: 16, color: theme.palette.success.main }} 
                                />
                              ) : (
                                <ErrorOutlineIcon 
                                  sx={{ fontSize: 16, color: theme.palette.error.main }} 
                                />
                              )}
                            </Tooltip>
                          )}
                        </Box>
                        {/* Format JSON button */}
                        <Tooltip title="Format JSON">
                          <IconButton
                            size="small"
                            onClick={() => handleFormatJson(payload.id)}
                            disabled={!payload.isValidJson || isLoading}
                            sx={{ 
                              color: payload.isValidJson ? theme.palette.primary.main : theme.palette.text.disabled,
                              p: 0.5
                            }}
                          >
                            <FormatAlignLeftIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <TextField
                        value={payload.response}
                        onChange={(e) => handleUpdatePayload(payload.id, 'response', e.target.value)}
                        multiline
                        rows={6}
                        fullWidth
                        size="small"
                        disabled={isLoading}
                        placeholder='{"key": "value"}'
                        error={payload.response !== '' && !payload.isValidJson}
                        helperText={payload.response !== '' && !payload.isValidJson ? 'Invalid JSON format' : ''}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.paper,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            '& textarea': {
                              resize: 'vertical'
                            }
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          sx={{ 
            textTransform: 'none'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading || payloads.some(p => !p.isValidJson)}
          sx={{ 
            textTransform: 'none',
            minWidth: 120
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Save Changes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

